// SPDX-License-Identifier: MIT
// Pure serialization of a project's PORTABLE state to/from the .genesis/ file set
// committed into the project's git repo. This is the single carrier for
// cross-workstation continuity (see the 2026-06-20 continuity redesign spec):
// "save" writes these files + commits + pushes; "resume" clones + parses + hydrates.
// Deterministic (sorted keys) so repeated saves don't create spurious commits.
import type { CloudSession } from './session-sync';

export const GENESIS_DIR = '.genesis';
export const GENESIS_SCHEMA_VERSION = 1;

export interface GenesisProjectFile {
  title: string;
  idea: string;
  outline: string[];
  first_steps: string[];
  stack: string | null;
  locale: string;
  discovery: {
    summary: string | null;
    questions: { id: string; question: string; placeholder?: string }[] | null;
    answers: Record<string, string> | null;
  };
  current_version_number: number;
}

export interface GenesisVersionFile {
  version_number: number;
  title: string;
  outline: string[];
  first_steps: string[];
  stack: string | null;
  context_kind: 'initial' | 'regen' | 'enriched' | 'discovery';
  context_snapshot: string | null;
  created_at: string;
}

export interface GenesisBackendFile {
  kind: 'byo-supabase' | 'genesis-rest' | 'full-supabase';
  url: string;
  anon_key: string;
}

// Identity only — STABLE across saves (no timestamp/author). Provenance (who/when)
// lives in the git commit (author + "Genesis: build <ts>" message), so meta.json
// produces no spurious diff → no content-free commit+push on every run (spec §5).
export interface GenesisMetaFile {
  schemaVersion: number;
  cloudProjectId: string;
}

export interface GenesisState {
  project: GenesisProjectFile | null; // null when a repo predates / lacks project.json
  versions: GenesisVersionFile[];
  sessions: CloudSession[];
  backend: GenesisBackendFile | null;
  meta: GenesisMetaFile;
}

// path (with .genesis/ prefix) -> JSON file contents
export type GenesisFileMap = Record<string, string>;

// Recursively sort object keys so the JSON output is stable for identical data.
function sortValue(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortValue);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>).sort()) {
      out[k] = sortValue((v as Record<string, unknown>)[k]);
    }
    return out;
  }
  return v;
}

export function stableStringify(v: unknown): string {
  return `${JSON.stringify(sortValue(v), null, 2)}\n`;
}

export function serializeGenesisState(s: GenesisState): GenesisFileMap {
  return {
    '.genesis/project.json': stableStringify(s.project),
    '.genesis/versions.json': stableStringify(s.versions),
    '.genesis/sessions.json': stableStringify(s.sessions),
    '.genesis/meta.json': stableStringify(s.meta),
    '.genesis/backend.json': stableStringify(s.backend),
  };
}

export function parseGenesisFiles(files: GenesisFileMap): GenesisState {
  const get = <T>(path: string, fallback: T): T => {
    const raw = files[path];
    if (typeof raw !== 'string' || raw.length === 0) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  };
  return {
    project: get<GenesisProjectFile | null>('.genesis/project.json', null),
    versions: get<GenesisVersionFile[]>('.genesis/versions.json', []),
    sessions: get<CloudSession[]>('.genesis/sessions.json', []),
    backend: get<GenesisBackendFile | null>('.genesis/backend.json', null),
    meta: get<GenesisMetaFile>('.genesis/meta.json', {
      schemaVersion: 0, cloudProjectId: '',
    }),
  };
}
